import { DropdownItem } from '@app/app/_components/Dropdown/DropdownItem';
import { DropdownOption } from '@app/app/_components/Dropdown/DropdownOption';
import { CloseIcon } from '@app/app/_icons/CloseIcon';
import { useCallback, useEffect, useRef, KeyboardEvent, forwardRef, Ref } from 'react';
import { twJoin } from 'tailwind-merge';

interface Props {
    options: DropdownOption[];
    placeholder?: string;
    selectedOption?: DropdownOption;
    onOptionClick?: (option: DropdownOption) => void;
    onEsc?: () => void;
    align?: 'right' | 'left';
    id?: string;
}

export const DropdownMenu = forwardRef(function DropdownMenuWithRef(
    { options, placeholder, selectedOption, onOptionClick, onEsc, align = 'right', id }: Props,
    ref: Ref<HTMLUListElement>,
) {
    const menuRef = useRef<HTMLDivElement>(null);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLUListElement>) => {
            if (e.key === 'Escape') {
                onEsc?.();
            }
        },
        [onEsc],
    );

    useEffect(() => {
        (menuRef.current?.firstChild as HTMLLIElement).focus();
    }, []);

    return (
        <>
            <div className="fixed inset-0 z-40 block bg-black opacity-45 md:hidden" />

            <ul
                ref={ref}
                className={twJoin(
                    'flex select-none flex-col overflow-hidden bg-white',
                    'fixed inset-x-0 bottom-0 z-50 rounded-t-lg',
                    'md:absolute md:bottom-auto md:top-8 md:max-w-prose',
                    'md:rounded-lg md:shadow-lg',
                    'animate-in slide-in-from-bottom-full',
                    'md:animate-in md:fade-in md:slide-in-from-top-10',
                    align === 'left' && 'md:left-0 md:right-auto',
                    align === 'right' && 'md:left-auto md:right-0',
                )}
                onKeyDown={handleKeyDown}
                role="menu"
                id={id}>
                <div className="flex items-center justify-between gap-2 border-b border-b-neutral-100 px-6 py-3 md:hidden">
                    <div className="text-2xl font-medium">{placeholder}</div>
                    <button className="p-1" onClick={onEsc}>
                        <CloseIcon className="size-6 text-neutral-500" />
                    </button>
                </div>
                <div
                    ref={menuRef}
                    className="flex max-h-[70vh] flex-col gap-1 overflow-y-auto bg-neutral-50 px-6 py-6 md:max-h-[50vh] md:gap-0 md:p-1">
                    {options.map((option, index) => (
                        <DropdownItem
                            key={index}
                            option={option}
                            selected={option.value != null && option.value === selectedOption?.value}
                            onClick={onOptionClick}>
                            <span className="hidden md:inline">{option.label}</span>
                            <span className="inline md:hidden">{option.extendedLabel ?? option.label}</span>
                        </DropdownItem>
                    ))}
                </div>
            </ul>
        </>
    );
});
